import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
  AntageneFileItem,
  AntageneFilesResponse,
  AntageneUploadService
} from '../../../services/antagene-upload.service';

@Component({
  selector: 'app-upload-antagene',
  imports: [CommonModule],
  templateUrl: './upload-antagene.component.html',
  styleUrl: './upload-antagene.component.scss'
})
export class UploadAntageneComponent implements OnInit {
  protected selectedFiles: File[] = [];
  protected loading = false;
  protected error = '';
  protected success = '';
  protected filesLoading = false;
  protected filesError = '';
  protected existingFiles: AntageneFileItem[] = [];

  constructor(
    private readonly antageneUploadService: AntageneUploadService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadFiles();
    this.testTbRoute();
  }

  protected onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.error = '';
    this.success = '';
    this.selectedFiles = input.files ? Array.from(input.files) : [];
  }

  protected async upload(): Promise<void> {
    if (this.selectedFiles.length === 0) {
      this.error = 'Veuillez selectionner au moins un fichier XLSX ou FASTA.';
      this.cdr.markForCheck();
      return;
    }

    const allowedFiles = this.selectedFiles.filter((file) => this.isAllowedFile(file.name));
    const rejectedFiles = this.selectedFiles
      .filter((file) => !this.isAllowedFile(file.name))
      .map((file) => file.name);

    if (allowedFiles.length === 0) {
      this.error = 'Aucun fichier valide. Extensions autorisees: .xlsx, .fasta, .fa';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.error = '';
    this.success = '';
    this.cdr.markForCheck();

    let successCount = 0;
    const failedFiles: string[] = [];
    const successLogs: string[] = [];

    for (const file of allowedFiles) {
      try {
        const response = await firstValueFrom(this.antageneUploadService.upload(file));
        console.log('Upload success:', response);
        
        // Afficher les logs de succès aussi
        if (response?.debug_logs && Array.isArray(response.debug_logs) && response.debug_logs.length > 0) {
          const logsText = response.debug_logs
            .map((log: string) => String(log).replace(/\[extract_fasta\] /, ''))
            .join('\n');
          successLogs.push(`[LOGS] ${file.name}:\n${logsText}`);
          console.log('Logs affichés pour succès:', response.debug_logs);
        }
        
        successCount += 1;
      } catch (err) {
        const httpErr = err as HttpErrorResponse;
        const errorObj = httpErr.error;
        const transportMessage = this.buildTransportErrorMessage(httpErr);
        
        console.error('Upload error:', errorObj);
        
        // Extraire le message d'erreur
        let errorMsg = '';
        if (transportMessage) {
          errorMsg = transportMessage;
        } else if (typeof errorObj === 'string') {
          errorMsg = errorObj;
        } else if (errorObj?.detail) {
          if (typeof errorObj.detail === 'string') {
            errorMsg = errorObj.detail;
          } else if (typeof errorObj.detail === 'object' && errorObj.detail.message) {
            errorMsg = String(errorObj.detail.message);
          } else if (typeof errorObj.detail === 'object') {
            errorMsg = `[Erreur] ${JSON.stringify(errorObj.detail).substring(0, 200)}`;
          }
        } else if (errorObj?.message) {
          errorMsg = String(errorObj.message);
        } else if (typeof errorObj === 'object') {
          errorMsg = `[Erreur] ${JSON.stringify(errorObj).substring(0, 200)}`;
        } else {
          errorMsg = `${httpErr.status} - ${httpErr.statusText}`;
        }
        
        // Extraire les logs - chercher dans plusieurs emplacements possibles
        let debugLogs: string[] = [];
        if (errorObj?.detail?.debug_logs && Array.isArray(errorObj.detail.debug_logs)) {
          debugLogs = errorObj.detail.debug_logs;
          console.log('Logs trouvés en detail.debug_logs:', debugLogs);
        } else if (errorObj?.debug_logs && Array.isArray(errorObj.debug_logs)) {
          debugLogs = errorObj.debug_logs;
          console.log('Logs trouvés en debug_logs:', debugLogs);
        } else if (errorObj?.detail?.errors && Array.isArray(errorObj.detail.errors)) {
          // Fallback si les logs sont sous 'errors'
          debugLogs = errorObj.detail.errors.filter((e: any) => typeof e === 'string');
          console.log('Logs trouvés en detail.errors:', debugLogs);
        }
        
        console.log(`Total logs extraits: ${debugLogs.length}`);
        
        const debugSection = debugLogs.length > 0
          ? `\n[LOGS]\n${debugLogs.map((log: string) => String(log).replace(/\[extract_fasta\] /, '')).join('\n')}`
          : '';
        failedFiles.push(`${file.name}:\n${errorMsg}${debugSection}`);
        this.cdr.markForCheck();
      }
    }

    this.loading = false;
    this.selectedFiles = [];
    this.loadFiles();

    const messages: string[] = [];
    if (successCount > 0) {
      messages.push(`${successCount} fichier(s) uploadé(s).`);
    }
    if (rejectedFiles.length > 0) {
      messages.push(`${rejectedFiles.length} fichier(s) ignoré(s) (extension invalide): ${rejectedFiles.join(', ')}`);
    }
    
    this.success = messages.join(' ');
    if (successLogs.length > 0) {
      this.success += '\n\n' + successLogs.join('\n\n');
    }

    if (failedFiles.length > 0) {
      this.error = `${failedFiles.length} échec(s):\n${failedFiles.join('\n')}`;
    }

    this.cdr.markForCheck();
  }

  private isAllowedFile(filename: string): boolean {
    const lower = filename.toLowerCase();
    return lower.endsWith('.xlsx') || lower.endsWith('.fasta') || lower.endsWith('.fa');
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    const transportMessage = this.buildTransportErrorMessage(err);
    if (transportMessage) {
      return transportMessage;
    }

    if (typeof err.error === 'string' && err.error.trim()) {
      return err.error;
    }
    if (err.error?.detail) {
      return String(err.error.detail);
    }
    if (err.error?.message) {
      return String(err.error.message);
    }
    if (err.status && err.statusText) {
      const requestInfo = this.buildRequestInfo(err.url);
      return `${err.status} - ${err.statusText}${requestInfo}`;
    }
    return 'Erreur inconnue';
  }

  private buildTransportErrorMessage(err: HttpErrorResponse): string {
    const requestInfo = this.buildRequestInfo(err.url);

    if (err.status === 504) {
      return `Timeout proxy/backend (HTTP 504): le backend Antagene n'a pas repondu a temps${requestInfo}.`;
    }

    if (err.status === 503) {
      return `Backend indisponible (HTTP 503): service temporairement hors ligne ou en redemarrage${requestInfo}.`;
    }

    if (err.status === 502) {
      return `Backend indisponible (HTTP 502): passerelle/proxy n'arrive pas a joindre le backend${requestInfo}.`;
    }

    if (err.status === 408) {
      return `Timeout requete (HTTP 408): la requete a expire avant la fin du traitement${requestInfo}.`;
    }

    if (err.status !== 0) {
      return '';
    }

    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    if (isOffline) {
      return `Reseau indisponible: le poste client est hors ligne${requestInfo}.`;
    }

    if (err.error instanceof ProgressEvent) {
      return `Erreur transport (HTTP 0): CORS, backend indisponible, probleme TLS/certificat ou coupure reseau${requestInfo}.`;
    }

    return `Erreur transport (HTTP 0): requete non aboutie vers le backend Antagene${requestInfo}.`;
  }

  private buildRequestInfo(url: string | null): string {
    const trimmed = String(url ?? '').trim();
    if (!trimmed) {
      return '';
    }
    return ` (url: ${trimmed})`;
  }

  protected refreshFiles(): void {
    this.loadFiles();
  }

  private loadFiles(): void {
    this.filesLoading = true;
    this.filesError = '';
    this.cdr.markForCheck();

    this.antageneUploadService.listFiles().subscribe({
      next: (res: AntageneFilesResponse) => {
        this.existingFiles = res.items ?? [];
        this.filesLoading = false;
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.existingFiles = [];
        this.filesLoading = false;
        this.filesError = this.getErrorMessage(err);
        this.cdr.markForCheck();
      }
    });
  }

  private testTbRoute(): void {
    console.log('CALLING testTbRoute...');
    this.antageneUploadService.testTbRoute('16').subscribe({
      next: (res: any) => {
        console.log('TEST_TB_ROUTE RESULT:', res);
      },
      error: (err: any) => {
        console.error('TEST_TB_ROUTE ERROR:', err);
      }
    });
  }
}
